import { Box } from '@components/basics/container';
import { Icons } from '@components/themes/default';
import { Icon } from '@components/basics/icon';
import { IBox } from '@components/basics/container/Box/IBox';
import React, { useEffect, useContext, useState, useRef } from 'react';
import { ThemeContext } from 'styled-components';
import SharingOption from './SharingOption';

interface ISharingMenu extends IBox.BaseProps {
	sharingUrl?: string;
	sharingTitle?: string;
	sharingDescription?: string;
}

const SharingMenuDefault: React.FC<ISharingMenu> = ({ sharingUrl, sharingTitle, sharingDescription }): JSX.Element => {
	const themeContext = useContext(ThemeContext);
	const buttonRef = useRef(null);
	const menuRef = useRef(null);
	const [navigatorCanShareNatively, setNavigatorCanShareNatively] = useState(false);

	const shareData = {
		url: sharingUrl,
		title: sharingTitle,
		text: sharingDescription
	};
	useEffect(() => {
		// Check whether web share API is available in current browser
		// and data is valid and permission has been granted
		navigator?.canShare && navigator.canShare(shareData) && setNavigatorCanShareNatively(true);
	}, []);

	const nativeShare = () => {
		navigator.share(shareData);
	};

	return (
		<Box
			as="menu"
			position="relative"
			width="100%"
			padding="0.875rem 0"
			display="flex"
			alignItems="center"
			justifyContent="center"
			sStyle={{ padding: '1rem 0' }}
			customCss={{
				'border-top': `1px solid ${themeContext.sharingMenu.trigger.dividerColor}`,
				'border-bottom': `1px solid ${themeContext.sharingMenu.trigger.dividerColor}`
			}}
		>
			<Box
				as="li"
				display="inline-flex"
				position="relative"
				customCss={{
					':focus-within > menu': {
						opacity: '1',
						zIndex: '10',
						transition: 'opacity .2s ease-in, z-index 0s 0s'
					}
				}}
			>
				{/* menu trigger (button) */}
				<Box
					as="a"
					role="button"
					ref={buttonRef}
					tabIndex={0 as never}
					display="inline-flex"
					alignItems="center"
					justifyContent="center"
					cursor="pointer"
					padding="0.5rem"
					backgroundColor={themeContext.sharingMenu.trigger.button.backgroundColor}
					customCss={{
						':hover': { backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover },
						':focus': { textDecoration: 'none' },
						':focus-visible': { backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover, outline: 'none' }
					}}
					aria-label="Teilen"
					title="Teilen"
					position="relative"
					onClick={() => {
						navigatorCanShareNatively && nativeShare();
					}}
				>
					<Icon
						width="1.5rem"
						height="1.5rem"
						fontSize={'1.5rem'}
						color={themeContext.sharingMenu.trigger.button.iconColor}
						code={Icons.shareBoxOutline}
						marginRight="0.38rem"
					/>
					<Box
						color={themeContext.sharingMenu.trigger.button.textColor}
						textTransform="uppercase"
						fontSize="0.875rem"
						fontStyle="normal"
						fontWeight="700"
						lineHeight="0.875rem"
						letterSpacing="0.08313rem"
					>
						Teilen
					</Box>
				</Box>
				{/* menu: show when native sharing option is not available */}
				{!navigatorCanShareNatively && (
					<Box
						as="menu"
						ref={menuRef}
						display="grid"
						gridTemplateColumns="1fr"
						cursor="default"
						transition="opacity .2s ease-in, z-index 0s .2s" // animation: out
						opacity="0"
						zIndex="-1"
						borderRadius="0.125rem"
						backgroundColor={themeContext.sharingMenu.menu.backgroundColor}
						position="absolute"
						top="-1.75rem"
						bottom="unset"
						left="50%"
						width="15rem"
						transform="translate(-50%, -100%)"
						boxShadow="0px 2px 6px 0px rgba(0, 0, 0, 0.25)"
						customCss={{
							'backdrop-filter': 'blur(4px)',
							'list-style': 'none',
							li: {
								'border-bottom': `1px solid ${themeContext.sharingMenu.menu.dividerColor}`,
								'&:last-of-type': {
									'border-bottom': '0 none'
								}
							}
						}}
						padding="0 0.5rem"
					>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="facebook"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="twitter"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="whatsapp"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="mail"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="print"
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default SharingMenuDefault;
